/**
 * CHAT-16 — host-gated tester preview override for the chat flag.
 *
 * Preview deployments share the prod backend, so flipping the global
 * `chat_cta_enabled` DB flag to test the chat UI would expose the dormant chat
 * to all production users. This provides a LOCAL override instead: a tester can
 * enable chat for their own browser without touching the shared flag.
 *
 * Toggle (persisted in localStorage so it survives navigation):
 *   - `?chatPreview=1` → set   localStorage['chat_preview'] = '1'
 *   - `?chatPreview=0` → clear  localStorage['chat_preview']
 *
 * SAFETY — host gate: the override is honored ONLY on non-production hosts
 * (Vercel preview deployments `*.vercel.app` and localhost). It must NEVER take
 * effect on the live production domain (afriqahome.com), so a prod user
 * appending `?chatPreview=1` sees nothing.
 *
 * Consumed at the single shared flag-resolution point (isChatCtaEnabled), so
 * both the contact CTA and the /messages surface honor the same override.
 */

const STORAGE_KEY = 'chat_preview';
const PARAM = 'chatPreview';

/**
 * Non-production hosts where the override is eligible: localhost (local dev) and
 * Vercel preview deployments (`*.vercel.app`). The production custom domain
 * (afriqahome.com) is neither, so it never matches — the override stays inert.
 */
function isPreviewHost(hostname: string): boolean {
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
  return hostname.endsWith('.vercel.app');
}

/**
 * Whether the local chat preview override is active for this browser/host.
 * Returns false during SSR, on production, and on any error.
 */
export function isChatPreviewActive(): boolean {
  // SSR / non-browser: no override.
  if (typeof window === 'undefined') return false;

  try {
    // Host gate FIRST so the override can never take effect on production — not
    // even the localStorage write — when a prod user appends ?chatPreview=1.
    if (!isPreviewHost(window.location.hostname)) return false;

    // Sync the toggle from the URL into localStorage so it persists across
    // navigation (the param need only be present once).
    const toggle = new URLSearchParams(window.location.search).get(PARAM);
    if (toggle === '1') {
      localStorage.setItem(STORAGE_KEY, '1');
    } else if (toggle === '0') {
      localStorage.removeItem(STORAGE_KEY);
    }

    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}
